import styled from 'styled-components';
import { useState } from 'react';
import { useParams } from 'react-router';
import { PanelLink } from '../SidePanel';
import { mobileMediaQuery } from '../../utils/responsive';
import { useUserCanEditFarm } from '../../utils/hooks';

const LI_PADDING_Y = '0.3rem';
const ICON_WIDTH = '1.5rem';

const hoverStyles = `
  && {
    border: var(--hover-border);
    padding: calc(${LI_PADDING_Y} - 1px) calc(var(--side-panel-padding) - 1px) !important;
  }
`;

export const NavMainItem = styled.li`
  max-width: 100%;
  padding: ${LI_PADDING_Y} var(--side-panel-padding);
  cursor: ${({ hoverable = true }) => (hoverable ? 'pointer' : 'default')};
  position: relative;
  display: flex;
  align-items: center;
  line-height: 1.2;
  svg {
    color: ${({ isActive, hovered }) =>
      isActive || hovered ? 'var(--text-color)' : 'var(--text-muted-color)'};
  }
  ${({ hoverable = true }) => hoverable && `&:hover { ${hoverStyles} }`}
  ${({ hovered }) => hovered && hoverStyles}
  ${({ isActive }) => isActive && 'background-color: var(--active-color);'}
  ${PanelLink} {
    flex: 1;
    padding: ${LI_PADDING_Y} 0 ${LI_PADDING_Y} var(--side-panel-padding);
    display: flex;
    align-items: center;
    width: 100%;
    height: 100%;
    margin: -${LI_PADDING_Y} calc(-1 * var(--side-panel-padding));
  }
`;

export const IconSpan = styled.span`
  width: ${({ width }) => width || ICON_WIDTH};
  display: flex;
  align-items: center;
  flex-shrink: 0;
`;

export const StickyHeader = styled.h4`
  position: sticky;
  top: 0;
  background-color: var(--side-panel-color);
  display: flex;
  z-index: 1;
  margin: calc(0px - var(--side-panel-padding));
  margin-bottom: 0;
  padding: var(--side-panel-padding);
`;

export const HiddenButton = styled.span`
  display: none;
  &:hover {
    text-decoration: underline;
  }

  /* Fill the full height of the parent NavMainItem element: */
  height: 25px;
  padding: ${LI_PADDING_Y} 0;
  margin: -${LI_PADDING_Y} 0;
`;

const inlineBlockButtonCss = `
  ${HiddenButton} {
    display: inline-block;
  }`;

export const HoverNavItem = styled(NavMainItem)`
  &:hover {
    ${inlineBlockButtonCss}
  }
  @media ${mobileMediaQuery} {
    ${inlineBlockButtonCss}
  }
`;

const StyledNameInput = styled.input`
  margin: -1px -2px;
  border: none;
  width: 100%;
`;

export const StyledNameDiv = styled.div`
  flex-grow: 1;
  flex-shrink: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

// will wrap the input for a textarea type input
const StyledWrappedNameInput = styled.textarea`
  margin: -1px -2px;
  border: none;
  width: 100%;
  resize: none; /* Optional: Disable resizing */
  overflow: auto; /* Optional: Add a scrollbar if the content is too long */
`;

export const WrappedNameDiv = styled.div`
  flex-grow: 1;
  flex-shrink: 1;
  overflow: hidden;
`;

export const EditableNameInput = ({
  name,
  update,
  renderDefault = () => name, // Render the name in the non-editing state
  className,
  isEditable = true,
  onDoubleClick,
  wrapped = false, // wrap the input for a textarea type input
  ...props
}) => {
  const [isEditing, setIsEditing] = useState(false);

  const onDoubleClickDiv = (e) => {
    if (onDoubleClick) onDoubleClick(e);
    if (!isEditable) return;
    setIsEditing(true);
  };

  const onBlur = (e) => {
    if (e.target.value.trim() !== '') {
      update(e.target.value);
    }
    setIsEditing(false);
  };
  const onKeyUp = (e) => {
    if (e.code === 'Enter') {
      e.target.blur();
    }
  };
  const onFocus = (e) => {
    e.target.select();
  };

  const InputComponent = wrapped ? StyledWrappedNameInput : StyledNameInput;
  const DivComponent = wrapped ? WrappedNameDiv : StyledNameDiv;

  return isEditing ? (
    <div className={`${className} flex-grow-1`}>
      <InputComponent
        // eslint-disable-next-line jsx-a11y/no-autofocus
        autoFocus
        onBlur={onBlur}
        onFocus={onFocus}
        onKeyUp={onKeyUp}
        defaultValue={name}
        {...props}
      />
    </div>
  ) : (
    <DivComponent
      onDoubleClick={onDoubleClickDiv}
      title={name}
      className={className}
    >
      {renderDefault(name)}
    </DivComponent>
  );
};

export const WrappedNameInput = (props) => (
  <EditableNameInput wrapped {...props} />
);

export const NameInput = ({
  isEditable = true, // Override to prevent editing
  ...props
}) => {
  const { farmId } = useParams();
  const userCanEditFarm = useUserCanEditFarm(farmId);
  return (
    <EditableNameInput isEditable={userCanEditFarm && isEditable} {...props} />
  );
};

export const MaxWidthNameInput = styled(NameInput)`
  input {
    max-width: ${({ maxWidth }) => maxWidth || '150px'};
  }
`;
